"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { EASE_SMOOTH } from "@/components/motion/constants";

export interface AboutTimelineItem {
  year: string;
  title: string;
  description: string;
}

interface AboutStoryTimelineProps {
  items: readonly AboutTimelineItem[];
}

export function AboutStoryTimeline({ items }: AboutStoryTimelineProps) {
  const reduced = useReducedMotion();

  return (
    <div className="relative mt-10 md:mt-12">
      <div
        className="absolute bottom-2 left-[11px] top-2 w-0.5 bg-gradient-to-b from-[var(--charity-gold)] via-[var(--programs-green)] to-[var(--charity-green-dark)]/30 md:left-[13px]"
        aria-hidden
      />

      <ol className="space-y-8">
        {items.map((item, index) => (
          <Reveal key={item.year} delay={index * 0.08}>
            <li className="relative flex gap-5 pl-1 md:gap-6">
              <motion.span
                className="relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-[var(--charity-gold)] bg-white shadow-sm md:h-7 md:w-7"
                initial={reduced ? false : { scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{
                  type: "spring",
                  stiffness: 380,
                  damping: 22,
                  delay: reduced ? 0 : index * 0.1,
                }}
              >
                <span className="h-2 w-2 rounded-full bg-[var(--programs-green)]" />
              </motion.span>

              <div className="min-w-0 pb-1">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--charity-gold-dark)]">
                  {item.year}
                </p>
                <h3 className="mt-1 font-display text-lg font-bold text-foreground md:text-xl">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted md:text-base">
                  {item.description}
                </p>
              </div>
            </li>
          </Reveal>
        ))}
      </ol>

      {!reduced && (
        <motion.div
          className="pointer-events-none absolute left-[11px] top-2 h-8 w-0.5 bg-[var(--charity-gold)] md:left-[13px]"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.2, ease: EASE_SMOOTH }}
          style={{ transformOrigin: "top" }}
          aria-hidden
        />
      )}
    </div>
  );
}
